import React, { Component, Fragment } from 'react'
import styled from 'styled-components'
import { media, ResponsiveContainer } from 'app/assets/styles'
import { FormattedMessage } from 'react-intl'
import { Breadcrumb } from 'app/common/breadcrumb'
import { H2, H4, H6, SmallText, styleH6, Text } from 'app/common/typography'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import { Buttons, OpeningStatus, ServicesList } from 'app/common/restaurant'
import { eventsDefinitions } from 'shared/store/metadata'
import type {
    Coordinates,
    Coupon as CouponType,
    Hour,
    RestaurantShortDTO
} from 'shared/types'
import { regex, restaurantServices } from 'shared/utils'
import { Pin } from 'app/assets/icons'
import { object, string } from 'yup'
import { ApplicationDownload } from 'app/common/application-download'
import { Collapsible } from 'app/common/collapsible'
import { Error, Input } from 'app/common/forms'
import { Formik } from 'formik'
import { Recaptcha } from 'app/common/recaptcha'
import type { RestaurantSubscribeForm } from 'shared/store/restaurant'
import { COLOR } from 'app-v2/design-tokens'
import type { StatusOpening } from 'shared/store/store-locator'
import { CouponsInfos } from './CouponsInfos'
import { Loader } from 'app/common/loader'

const { DRIVE } = restaurantServices
const { red, lightWhite } = oldColors

type Props = {
    id: string,
    name: string,
    address: string,
    distance?: string,
    phone: string,
    status: StatusOpening,
    hour?: string,
    deliverooUrl?: string,
    uberEatsUrl: string,
    restaurantId: string,
    email: string,
    services: Array<string>,
    coordinates: Coordinates,
    userPosition?: Coordinates,
    openHours: Array<Hour>,
    openingHour: string,
    closingHour: string,
    coupons: Array<CouponType>,
    couponsLoaded: boolean,
    couponsError?: string,
    sendMailSuccess?: boolean,
    sendMailError?: string,
    sendMail: RestaurantSubscribeForm => void,
    cacStatus: string,
    cadStatus?: string,
    pageUrl: string,
    restaurant?: RestaurantShortDTO
}

type State = {
    isOpened: boolean,
    couponIndex: ?number,
    showCaptcha: boolean
}

export class InfosComponent extends Component<Props, State> {
    recaptchaPromise: Promise<string>
    resolve: any
    reject: any

    constructor() {
        super()
        this.state = {
            isOpened: false,
            couponIndex: null,
            showCaptcha: false
        }
        this.initRecaptchaPromise()
    }

    initRecaptchaPromise = () => {
        this.recaptchaPromise = new Promise((resolve, reject) => {
            this.resolve = resolve
            this.reject = reject
        })
    }

    verifyCallback = (recaptchaToken: string) => {
        if (!!recaptchaToken) {
            this.resolve(recaptchaToken)
        } else {
            this.reject('Error getting recaptcha token')
        }
    }

    toggleHours = (e: Event) => {
        e.preventDefault()
        this.setState({
            isOpened: !this.state.isOpened
        })
    }

    formSubscription = () => {
        const mandatory = 'form.error.field.mandatory'
        const invalid = {
            message: 'form.error.regex.email',
            excludeEmptyString: true
        }
        return object().shape({
            email: string()
                .required(mandatory)
                .matches(regex.EMAIL, invalid)
        })
    }

    render() {
        const {
            id,
            name,
            address,
            distance,
            phone,
            hour,
            services,
            coordinates,
            userPosition,
            deliverooUrl,
            uberEatsUrl,
            openHours,
            coupons,
            couponsLoaded,
            restaurantId,
            email,
            couponsError,
            sendMail,
            sendMailSuccess,
            sendMailError,
            cacStatus,
            driveStatus,
            pageUrl,
            status,
            openingHour,
            closingHour
        } = this.props
        const { isOpened, showCaptcha } = this.state

        let metaRestaurantSheet = { ...eventsDefinitions.RESTAURANT_SHEET }
        metaRestaurantSheet.event_label = name
        let metaRestaurantDirections = {
            ...eventsDefinitions.RESTAURANT_DIRECTIONS,
            event_label: name
        }
        const hasDrive = services.includes(DRIVE)
        return (
            <RestaurantInfos>
                <StyledResponsiveContainer>
                    <Breadcrumb
                        breadcrumbArray={[
                            {
                                title: `\u00A0>\u00A0${name}`
                            }
                        ]}
                    />
                    <StyledH2>{name.toLowerCase()}</StyledH2>
                    <Location>
                        <IconContainer>
                            <Pin />
                        </IconContainer>
                        <Address>
                            <StyledText>{name.toLowerCase()}</StyledText>
                            <Text>{address}</Text>
                            <Text>{phone}</Text>
                            <OpeningStatus
                                id={id}
                                status={status}
                                driveStatus={driveStatus}
                                openingHour={openingHour}
                                closingHour={closingHour}
                            />
                        </Address>
                    </Location>
                    <Info>
                        <FormattedMessage id="restaurant.offers.title">
                            {(text: string) => <H4>{text}</H4>}
                        </FormattedMessage>
                        <Loader loading={!couponsLoaded} className="restaurant">
                            <Fragment>
                                {couponsError ? (
                                    <FormattedMessage id={couponsError}>
                                        {(text: string) => (
                                            <Text color="black">{text}</Text>
                                        )}
                                    </FormattedMessage>
                                ) : (
                                    <Fragment>
                                        {coupons.length > 0 ? (
                                            <Fragment>
                                                <CouponsInfos
                                                    restaurantId={restaurantId}
                                                />
                                                <SubscribeContainer>
                                                    <Block>
                                                        <Formik
                                                            initialValues={{
                                                                email
                                                            }}
                                                            onSubmit={async (
                                                                values,
                                                                actions
                                                            ) => {
                                                                try {
                                                                    this.setState(
                                                                        {
                                                                            showCaptcha: true
                                                                        }
                                                                    )
                                                                    const recaptchaToken = await this
                                                                        .recaptchaPromise
                                                                    await sendMail(
                                                                        {
                                                                            restaurantId,
                                                                            email:
                                                                                values.email,
                                                                            kingdomId: email,
                                                                            queen: recaptchaToken
                                                                        }
                                                                    )
                                                                    actions.resetForm()
                                                                } finally {
                                                                    this.setState(
                                                                        {
                                                                            showCaptcha: false
                                                                        }
                                                                    )
                                                                    this.initRecaptchaPromise()
                                                                    actions.setSubmitting(
                                                                        false
                                                                    )
                                                                }
                                                            }}
                                                            validationSchema={this.formSubscription()}
                                                            render={({
                                                                errors,
                                                                touched,
                                                                isSubmitting,
                                                                handleSubmit
                                                            }) => (
                                                                <Form>
                                                                    {showCaptcha && (
                                                                        <Recaptcha
                                                                            action="WebRestaurantInfos"
                                                                            verifyCallback={
                                                                                this
                                                                                    .verifyCallback
                                                                            }
                                                                        />
                                                                    )}
                                                                    <FormattedMessage id="restaurant.offers.mail.label">
                                                                        {(
                                                                            text: string
                                                                        ) => (
                                                                            <SmallText>
                                                                                {
                                                                                    text
                                                                                }
                                                                            </SmallText>
                                                                        )}
                                                                    </FormattedMessage>
                                                                    <InputContainer>
                                                                        <Input
                                                                            name="email"
                                                                            type="email"
                                                                            placeholderKey="input.email.placeholder"
                                                                            className={`restaurant ${errors.email &&
                                                                                touched.email &&
                                                                                'error'}`}
                                                                        />
                                                                        <FormattedMessage id="footer.newsletter.submit">
                                                                            {(
                                                                                t: string
                                                                            ) => (
                                                                                <StyledButton
                                                                                    disabled={
                                                                                        isSubmitting
                                                                                    }
                                                                                    onClick={
                                                                                        handleSubmit
                                                                                    }
                                                                                    color={
                                                                                        COLOR.PRIMARY
                                                                                    }>
                                                                                    {
                                                                                        t
                                                                                    }
                                                                                </StyledButton>
                                                                            )}
                                                                        </FormattedMessage>
                                                                    </InputContainer>
                                                                    {errors.email &&
                                                                        touched.email && (
                                                                            <Error
                                                                                color="black"
                                                                                errorKey={
                                                                                    errors.email
                                                                                }
                                                                            />
                                                                        )}
                                                                    {sendMailSuccess && (
                                                                        <FormattedMessage id="restaurant.offfers.suscribe.success">
                                                                            {(
                                                                                text: string
                                                                            ) => (
                                                                                <SmallText
                                                                                    color={
                                                                                        colors.crunchyGreen
                                                                                    }>
                                                                                    {
                                                                                        text
                                                                                    }
                                                                                </SmallText>
                                                                            )}
                                                                        </FormattedMessage>
                                                                    )}
                                                                    {sendMailError && (
                                                                        <FormattedMessage
                                                                            id={
                                                                                sendMailError
                                                                            }>
                                                                            {(
                                                                                text: string
                                                                            ) => (
                                                                                <SmallText
                                                                                    color={
                                                                                        red
                                                                                    }>
                                                                                    {
                                                                                        text
                                                                                    }
                                                                                </SmallText>
                                                                            )}
                                                                        </FormattedMessage>
                                                                    )}
                                                                </Form>
                                                            )}
                                                        />
                                                    </Block>
                                                    <Separator />
                                                    <FormattedMessage id="or">
                                                        {(text: string) => (
                                                            <Or color="white">
                                                                {text}
                                                            </Or>
                                                        )}
                                                    </FormattedMessage>
                                                    <Block>
                                                        <FormattedMessage id="restaurant.offers.applications.label">
                                                            {(text: string) => (
                                                                <SmallText>
                                                                    {text}
                                                                </SmallText>
                                                            )}
                                                        </FormattedMessage>
                                                        <ApplicationDownload />
                                                    </Block>
                                                </SubscribeContainer>
                                            </Fragment>
                                        ) : (
                                            <FormattedMessage id="restaurant.offers.none">
                                                {(text: string) => (
                                                    <Text color="black">
                                                        {text}
                                                    </Text>
                                                )}
                                            </FormattedMessage>
                                        )}
                                    </Fragment>
                                )}
                            </Fragment>
                        </Loader>
                    </Info>
                    <Info onClick={this.toggleHours} pointer>
                        <Collapsible
                            labelKey="restaurant.hours.label"
                            isOpened={isOpened}
                            color={COLOR.PRIMARY}>
                            <Fragment>
                                <Line>
                                    <Day />
                                    <Hours>
                                        <FormattedMessage id="restaurant.room">
                                            {(text: string) => (
                                                <StyledHour>{text}</StyledHour>
                                            )}
                                        </FormattedMessage>
                                        {hasDrive ? (
                                            <FormattedMessage id="restaurant.drive">
                                                {(text: string) => (
                                                    <StyledHour>
                                                        {text}
                                                    </StyledHour>
                                                )}
                                            </FormattedMessage>
                                        ) : (
                                            <StyledHour />
                                        )}
                                    </Hours>
                                </Line>
                                {openHours.map(hour => {
                                    const { day, drive, room } = hour
                                    return (
                                        <Line key={day}>
                                            <Day>
                                                <FormattedMessage id={day}>
                                                    {(text: string) => (
                                                        <Text>{text}</Text>
                                                    )}
                                                </FormattedMessage>
                                                <Dashed />
                                            </Day>
                                            <Hours>
                                                {room ? (
                                                    <StyledHour>
                                                        {room}
                                                    </StyledHour>
                                                ) : (
                                                    <FormattedMessage id="restaurant.closed">
                                                        {(text: string) => (
                                                            <StyledHour>
                                                                {text}
                                                            </StyledHour>
                                                        )}
                                                    </FormattedMessage>
                                                )}
                                                {hasDrive && (
                                                    <Fragment>
                                                        {drive ? (
                                                            <StyledHour>
                                                                {drive}
                                                            </StyledHour>
                                                        ) : (
                                                            <FormattedMessage id="restaurant.closed">
                                                                {(
                                                                    text: string
                                                                ) => (
                                                                    <StyledHour>
                                                                        {text}
                                                                    </StyledHour>
                                                                )}
                                                            </FormattedMessage>
                                                        )}
                                                    </Fragment>
                                                )}
                                            </Hours>
                                        </Line>
                                    )
                                })}
                            </Fragment>
                        </Collapsible>
                    </Info>
                    <Info>
                        {services && services.length > 0 && (
                            <ServicesList services={services} />
                        )}
                    </Info>
                    <Buttons
                        origin={userPosition && userPosition}
                        destination={coordinates}
                        metaDirection={metaRestaurantDirections}
                        deliverooUrl={deliverooUrl}
                        uberEatsUrl={uberEatsUrl}
                        className="restaurant"
                        services={services}
                        cacStatus={cacStatus}
                        restaurantParams={{
                            name: name.split('BURGER KING ')[1],
                            id,
                            hour,
                            addressFull: address,
                            services,
                            cacStatus,
                            distance,
                            deliverooUrl,
                            uberEatsUrl,
                            pageUrl
                        }}
                    />
                </StyledResponsiveContainer>
            </RestaurantInfos>
        )
    }
}

const RestaurantInfos = styled.div`
    width: 100%;
`

const StyledResponsiveContainer = styled(ResponsiveContainer)`
    margin-top: 30px;
    margin-bottom: 30px;
    ${media.medium`
        margin-top: 60px;
        margin-bottom: 100px;
    `};
    ${media.large`
        margin-left: calc((100vw - 1280px)/ 2);
        /* Calculate from wireframes => 1280px / 2 - marginRight: 120px */
        width: 520px;
        margin-right: 120px;
    `};
`

const Info = styled.div`
    padding-bottom: 20px;
    padding-top: 20px;
    border-bottom: 1px solid ${lightWhite};
    ${props => props.pointer && 'cursor: pointer'};
`

const Location = styled(Info)`
    display: flex;
    align-items: flex-start;
`
const IconContainer = styled.div`
    padding-right: 5px;
`

const Address = styled.div`
    display: flex;
    flex-direction: column;
`

const Line = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    width: 100%;
`

const Day = styled.div`
    display: flex;
    align-items: baseline;
    width: calc(100% - 205px);
    ${media.medium`
        width: calc(100% - 230px);
    `};
`

const Dashed = styled.span`
    margin-left: 5px;
    border: 1px dashed ${lightWhite};
    width: 100%;
`

const Hours = styled.div`
    display: flex;
    justify-content: space-between;
    width: 200px;
    padding-left: 5px;
    ${media.medium`
        width: 220px;
        padding-left: 10px;
    `};
`

const StyledHour = styled(Text)`
    width: 45%;
    text-align: center;
`

const Form = styled.div`
    margin-bottom: 20px;
`

const SubscribeContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    position: relative;
`

const Separator = styled.div`
    background-color: ${lightWhite};
    height: 1px;
    width: 50%;
    margin: 20px auto;
    ${media.medium`
        height: 135px;
        width: 1px;
    `};
`

const Or = styled(H6)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: ${COLOR.PRIMARY};
    border-radius: 50%;
    padding: 5px;
    line-height: 16px;
    margin: 10px auto;
    ${media.medium`
        line-height: 24px;
        margin: auto;
    `};
`

const InputContainer = styled.div`
    position: relative;
    margin-top: 8px;
`

const Block = styled.div`
    width: 100%;
    margin-top: 10px;
    ${media.medium`
        margin-top: 20px;
        width: 44%;
    `};
`

const StyledButton = styled.button`
    ${styleH6};
    background: none;
    border: none;
    cursor: ${props => (props.disabled ? 'none' : 'pointer')};
    position: absolute;
    z-index: 2;
    top: 50%;
    transform: translateY(-50%);
    left: calc(100% - 50px);
`

const StyledH2 = styled(H2)`
    text-transform: capitalize;
`

const StyledText = styled(Text)`
    text-transform: capitalize;
`
